import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
  Loader2,
  Send,
  FileText,
  FilePlus2,
  Download,
  Paperclip,
  Mic,
  Square,
  ArrowDown,
  Volume2,
  VolumeX,
  X,
  Image as ImageIcon,
} from "lucide-react";
import { downloadDocumentById } from "@/lib/downloadDocumentById";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { extractFirstName } from "@/lib/stewWelcome";
import { stripDocIdPrefix } from "@/lib/docTitle";
import { stripDocCodes } from "@/lib/stripDocCodes";
import { useDocTitles } from "@/lib/docTitles";


import { Link, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import {
  STEW_TOOL_REGISTRY,
  parseStewTools,
  type StewToolKey,
} from "@/lib/stewToolRegistry";
import {
  StewDocumentReview,
  type StewGeneratedDoc,
} from "./StewDocumentReview";
import { StewSignFlow, type StewSignTarget } from "./StewSignFlow";
import { StewFeedbackControl } from "./StewFeedbackControl";
import { StewAvatar } from "./StewAvatar";
import {
  ACCEPT_ATTR,
  ACCEPTED_MIME,
  MAX_FILE_BYTES,
  MAX_FILES_PER_MESSAGE,
  buildAttachmentContext,
  detectKind,
  prepareAttachment,
  uploadAttachment,
  type PreparedAttachment,
} from "@/lib/stewAttachments";
import {
  isSpeechSynthesisSupported,
  markdownToSpeech,
  speak,
  stopSpeaking,
  useVoiceRecorder,
  RECORD_MAX_SECONDS,
  RECORD_WARN_AT,
} from "@/lib/stewVoice";
import { logError } from "@/lib/errorLogger";
import {
  createSupportTicket,
  newSubmissionKey,
  syncTicketToHelpDesk,
} from "@/lib/supportTicket";


function currentRoute(): string {
  return typeof window !== "undefined" ? window.location.pathname : "";
}

function supabaseErrContext(err: any, extra: Record<string, unknown>) {
  return {
    code: err?.code ?? null,
    message: err?.message ?? String(err ?? ""),
    details: err?.details ?? null,
    hint: err?.hint ?? null,
    status: err?.status ?? null,
    name: err?.name ?? null,
    ...extra,
  };
}

function logStewFailure(
  errorClass: string,
  err: any,
  context: Record<string, unknown>,
) {
  void logError({
    errorClass,
    route: currentRoute(),
    statusCode: err?.status ?? null,
    message: err?.message || String(err ?? "unknown"),
    stack: err?.stack,
    context: supabaseErrContext(err, context),
  }).catch(() => {});
}


export interface StewSource {
  doc_id: string;
  title: string;
  collection: "sop" | "faq" | string;
  status?: string;
}

type Classification = {
  record_type: "resolution" | "journal" | "minutes";
  suggested_title?: string;
  rationale?: string;
  prefill?: Record<string, any>;
};

type IntentOffer = {
  messageId?: string;
  classification: Classification;
  sourceText: string;
  registryDisplayName: string;
  revisionOf?: string | null;
  used?: boolean;
};

interface StewAttachmentMeta {
  name: string;
  kind: "pdf" | "text" | "docx" | "image";
  path: string | null;
  truncated?: boolean;
}

interface StewMessage {
  id?: string;
  clientId?: string;
  role: "user" | "assistant";
  content: string;
  sources?: StewSource[];
  offer?: IntentOffer;
  pending?: boolean;
  documentId?: string;
  attachments?: StewAttachmentMeta[];
  crisisFlag?: "hard" | "soft" | false;
  deflection?: boolean;
  tools?: StewToolKey[];
  failed?: boolean;
  retry?: { text: string; attachments: PreparedAttachment[] };
  escalation?: {
    sent: boolean;
    sending: boolean;
  };
}

interface StewChatProps {
  conversationId: string | null;
  onConversationCreated: (id: string) => void;
  onConversationTouched: () => void;
  includeUnpublished?: boolean;
  disableDrafting?: boolean;
  surface?: "member" | "internal";
  initialMessage?: string | null;
  onInitialMessageConsumed?: () => void;
  /**
   * Display-level filter for concierge tool chips. Keys listed here are hidden
   * from render only; the underlying message metadata and tool machinery are
   * untouched. Used by surfaces (like /stew) that must not route members away.
   */
  hiddenToolKeys?: StewToolKey[];
  /**
   * When true, the chat root fills its parent container height (used by the
   * standalone /stew surface which owns its own card chrome). Default false
   * keeps the Support tab's existing self-sized layout.
   */
  fillContainer?: boolean;
}


function titleFromMessage(text: string): string {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (cleaned.length <= 60) return cleaned || "New conversation";
  return cleaned.slice(0, 57).trimEnd() + "...";
}

function sourceHref(s: StewSource): string {
  return s.collection === "faq" ? `/support/library/${s.doc_id}` : `/support/library/${s.doc_id}`;
}

const RECORD_LABEL: Record<Classification["record_type"], string> = {
  resolution: "Draft this resolution",
  journal: "Record this journal entry",
  minutes: "Start these meeting minutes",
};

const REGISTRY_DOCTYPE: Record<Classification["record_type"], string> = {
  resolution: "stew_resolution",
  journal: "stew_journal_entry",
  minutes: "stew_meeting_minutes",
};

export function StewChat({
  conversationId,
  onConversationCreated,
  onConversationTouched,
  includeUnpublished = false,
  disableDrafting = false,
  surface = "member",
  initialMessage = null,
  onInitialMessageConsumed,
  hiddenToolKeys,
  fillContainer = false,
}: StewChatProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState<string | null>(null);
  const [unaId, setUnaId] = useState<string | null>(null);
  const docTitles = useDocTitles();
  const [messages, setMessages] = useState<StewMessage[]>([]);

  const [input, setInput] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState<string | null>(null); // offer key being drafted
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [reviewDoc, setReviewDoc] = useState<StewGeneratedDoc | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [signTarget, setSignTarget] = useState<StewSignTarget | null>(null);
  const [signOpen, setSignOpen] = useState(false);
  const [pendingRevision, setPendingRevision] = useState<{
    parentId: string;
    note: string;
    registryDisplayName: string;
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<PreparedAttachment[]>([]);
  const [preparingFile, setPreparingFile] = useState(false);
  const [voiceRepliesOn, setVoiceRepliesOn] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const sentViaVoiceRef = useRef(false);
  const voice = useVoiceRecorder(
    (text) => {
      // Append transcript to any existing composer text with a space so the
      // member can edit before sending. Auto-grow effect handles the height.
      setInput((prev) => {
        const trimmed = prev.trimEnd();
        return trimmed ? `${trimmed} ${text}` : text;
      });
      // Focus the composer so the caret lands at the end of the appended text.
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    (message) => {
      toast({
        title: "Voice input",
        description: message,
        variant: "destructive",
      });
    },
  );
  const isMobile = useIsMobile();
  const ttsSupported = isSpeechSynthesisSupported();
  // Set to the conversation id that ensureConversation just created during an
  // active send, so the history-load effect can skip a redundant refetch that
  // would wipe the optimistic bubble. Cleared on the next real switch.
  const justCreatedConvIdRef = useRef<string | null>(null);

  // Auto-grow the textarea from 1 row up to ~6 rows, then scroll internally.
  // Keep the caret / end of transcript in view as content streams in.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const line = 24; // approx line height in px
    const max = line * 6 + 20;
    const next = Math.min(el.scrollHeight, max);
    el.style.height = next + "px";
    // Only paint a scrollbar once content actually overflows the six-line cap.
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
    if (el.scrollHeight > max) {
      el.scrollTop = el.scrollHeight;
    }
  }, [input]);

  // "Near bottom" tracking so we don't yank the reader down while they scroll up.
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const nearBottomRef = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      const near = distance < 96;
      nearBottomRef.current = near;
      setShowJumpToLatest(!near);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);


  // Fetch first name + una once
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const [{ data: prof }, { data: unaOwned }] = await Promise.all([
        supabase
          .from("profiles")
          .select("full_name, display_name")
          .eq("id", user.id)
          .maybeSingle(),
        supabase
          .from("unas")
          .select("id")
          .eq("treasury_secretary_id", user.id)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      setFirstName(extractFirstName(prof as any));
      let una = unaOwned?.id ?? null;
      if (!una) {
        const { data: mem } = await supabase
          .from("una_members")
          .select("una_id")
          .eq("user_id", user.id)
          .eq("status", "active")
          .maybeSingle();
        una = mem?.una_id ?? null;
      }
      if (!cancelled) setUnaId(una);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const welcome = useMemo(
    () => ({
      role: "assistant" as const,
      content: firstName
        ? `Hi ${firstName}, I'm Stew, your stewardship assistant. Ask me anything, or tell me what you're working on.`
        : `Hi, I'm Stew, your stewardship assistant. Ask me anything, or tell me what you're working on.`,
    }),
    [firstName],
  );

  // Load history when conversationId changes
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setLoadingHistory(false);
      return;
    }
    // Skip the refetch when this conversationId change was caused by
    // ensureConversation mid-send: the optimistic bubbles and in-flight
    // insert must not be clobbered.
    if (justCreatedConvIdRef.current === conversationId) {
      justCreatedConvIdRef.current = null;
      setLoadingHistory(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      const { data, error } = await supabase
        .from("stew_messages")
        .select("id, role, content, sources, metadata, created_at")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });
      if (cancelled) return;
      if (error) {
        console.error("StewChat load conversation failed:", error);
        toast({
          title: "That conversation didn't load",
          description:
            "Give it a moment and try again. If it keeps happening, open a ticket.",
          variant: "destructive",
        });
      } else {
        setMessages(
          (data || [])
            .filter((m: any) => m.role === "user" || m.role === "assistant")
            .map((m: any) => {
              const rawContent: string = m.content || "";
              // Defensive strip of any legacy TOOLS: line stored in content.
              const parsed =
                m.role === "assistant"
                  ? parseStewTools(rawContent)
                  : { text: rawContent, tools: [] as StewToolKey[] };
              const storedTools: StewToolKey[] = Array.isArray(
                m.metadata?.tools,
              )
                ? (m.metadata.tools as any[])
                    .filter((k: any): k is string => typeof k === "string")
                    .filter((k): k is StewToolKey => k in STEW_TOOL_REGISTRY)
                    .slice(0, 2)
                : parsed.tools;
              return {
                id: m.id,
                role: m.role,
                content: parsed.text,
                sources: Array.isArray(m.sources) ? m.sources : [],
                offer: (m.metadata && m.metadata.offer) || undefined,
                documentId:
                  m.metadata?.signed_document_id ||
                  m.metadata?.approved_document_id ||
                  undefined,
                tools: storedTools,
              };
            }),
        );
      }
      setLoadingHistory(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, toast]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!nearBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);


  useEffect(() => {
    inputRef.current?.focus();
  }, [conversationId, sending]);

  const ensureConversation = useCallback(
    async (firstUserText: string): Promise<string | null> => {
      if (conversationId) return conversationId;
      if (!user) return null;
      const title = titleFromMessage(firstUserText);
      const { data, error } = await supabase
        .from("stew_conversations")
        .insert({ user_id: user.id, una_id: unaId, title })
        .select("id")
        .single();
      if (error || !data) {
        console.error("StewChat start conversation failed:", error);
        logStewFailure("stew_conversation_create_failed", error, {
          conversation_id: null,
          una_id: unaId,
        });
        return null;
      }
      // Guard the history-load effect from wiping the optimistic bubble when
      // the parent flips conversationId in response to onConversationCreated.
      justCreatedConvIdRef.current = data.id;
      onConversationCreated(data.id);
      return data.id;
    },
    [conversationId, user, unaId, onConversationCreated],
  );

  // Classify silently; return offer or null on 422/unclassifiable.
  const classifyIntent = useCallback(
    async (text: string): Promise<Classification | null> => {
      if (!unaId) return null;
      try {
        const { data, error } = await supabase.functions.invoke(
          "classify-governance-intent",
          { body: { unaId, text } },
        );
        if (error) return null;
        const c = (data as any)?.classification;
        if (!c?.record_type) return null;
        return c as Classification;
      } catch {
        return null;
      }
    },
    [unaId],
  );

  const send = useCallback(async (
    overrideText?: string,
    retryContext?: { attachments: PreparedAttachment[]; clientId?: string },
  ) => {
    const q = (overrideText ?? input).trim();
    const sourceAttachments = retryContext?.attachments ?? attachments;
    if ((!q && sourceAttachments.length === 0) || sending || !user) return;

    const wasVoice = sentViaVoiceRef.current;
    sentViaVoiceRef.current = false;

    // Snapshot and clear attachments locally so the user sees them detach.
    const pending = sourceAttachments;
    if (!retryContext) setAttachments([]);

    setSending(true);
    if (!overrideText) setInput("");

    const historyForApi = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    // Upload attachments to the private bucket (best-effort; failures do not
    // block the message, the extracted context is still sent).
    const uploaded: StewAttachmentMeta[] = [];
    for (const att of pending) {
      try {
        const path = await uploadAttachment(user.id, att);
        att.storagePath = path;
        uploaded.push({
          name: att.file.name,
          kind: att.kind,
          path,
          truncated: att.truncated,
        });
      } catch (e) {
        console.error("stew-attachments upload failed:", e);
        uploaded.push({
          name: att.file.name,
          kind: att.kind,
          path: null,
          truncated: att.truncated,
        });
      }
    }

    const { contextText } = buildAttachmentContext(pending);
    const imagePaths = pending
      .filter((a) => a.kind === "image" && a.storagePath)
      .map((a) => a.storagePath as string);
    const displayText = q || (uploaded.length ? "(shared files)" : "");
    const augmented =
      contextText.length > 0
        ? `${q}\n\n---\n\n${contextText}`
        : q;

    const clientId =
      retryContext?.clientId ||
      (typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const retryPayload = { text: q, attachments: pending };

    const optimisticUser: StewMessage = {
      clientId,
      role: "user",
      content: displayText,
      attachments: uploaded,
    };
    // If this is a retry, drop any prior failed bubble with the same clientId
    // first so we don't render duplicates.
    setMessages((prev) => [
      ...prev.filter((m) => m.clientId !== clientId),
      optimisticUser,
    ]);

    const markFailed = () => {
      setMessages((prev) =>
        prev.map((m) =>
          m.clientId === clientId
            ? { ...m, failed: true, retry: retryPayload }
            : m,
        ),
      );
    };

    const convId = await ensureConversation(displayText || "New conversation");
    if (!convId) {
      markFailed();
      setSending(false);
      return;
    }

    const userInsertPayload: Record<string, any> = {
      conversation_id: convId,
      role: "user",
      content: displayText,
    };
    if (uploaded.length) {
      userInsertPayload.metadata = { attachments: uploaded };
    }
    const { error: userInsertErr } = await supabase
      .from("stew_messages")
      .insert(userInsertPayload as any);
    if (userInsertErr) {
      console.error("StewChat save user message failed:", userInsertErr);
      logStewFailure("stew_user_message_insert_failed", userInsertErr, {
        conversation_id: convId,
      });
      markFailed();
      setSending(false);
      return;
    }

    try {
      // Fire manual-assistant reply and classifier in parallel.
      const revisionCtx = pendingRevision;
      const classifierText = revisionCtx
        ? `Revision of prior ${revisionCtx.registryDisplayName}. Member note: ${revisionCtx.note}. Original context: ${q}`
        : q;

      const [replyRes, classification] = await Promise.all([
        supabase.functions.invoke("manual-assistant", {
          body: {
            question: augmented || displayText,
            history: historyForApi.slice(-8),
            ...(imagePaths.length ? { image_paths: imagePaths } : {}),
            ...(includeUnpublished ? { include_unpublished: true } : {}),
          },
        }),
        disableDrafting ? Promise.resolve(null) : classifyIntent(classifierText),
      ]);

      if (replyRes.error) throw replyRes.error;
      const data = replyRes.data as any;
      const rawAnswer: string =
        data?.answer || "I couldn't produce an answer. Please try again.";
      // Server strips the TOOLS: line, but defensively strip again in case an
      // older function version is deployed. Prefer server-parsed tools when
      // present.
      const defensiveParsed = parseStewTools(rawAnswer);
      let answer = defensiveParsed.text;
      const serverTools: StewToolKey[] = Array.isArray(data?.tools)
        ? (data.tools as any[])
            .filter((k: any): k is string => typeof k === "string")
            .filter((k): k is StewToolKey => k in STEW_TOOL_REGISTRY)
            .slice(0, 2)
        : [];
      const tools: StewToolKey[] =
        serverTools.length > 0 ? serverTools : defensiveParsed.tools;
      const sources: StewSource[] = Array.isArray(data?.sources)
        ? data.sources
        : [];

      // Registry lookup for label
      let registryDisplayName = "";
      if (classification) {
        const dtype = REGISTRY_DOCTYPE[classification.record_type];
        const { data: reg } = await supabase
          .from("stew_document_registry")
          .select("display_name")
          .eq("document_type", dtype)
          .maybeSingle();
        registryDisplayName = reg?.display_name || "";
      }

      const offer: IntentOffer | undefined = classification
        ? {
            classification,
            sourceText: q,
            registryDisplayName,
            revisionOf: revisionCtx?.parentId ?? null,
          }
        : undefined;

      const { data: inserted, error: aInsertErr } = await supabase
        .from("stew_messages")
        .insert({
          conversation_id: convId,
          role: "assistant",
          content: answer,
          sources: sources as any,
          metadata: {
            flagged_crisis: !!data?.flagged_crisis,
            offer: offer ?? null,
            tools: tools.length > 0 ? tools : null,
          } as any,
        })
        .select("id")
        .single();

      if (aInsertErr) {
        console.error("StewChat save reply failed:", aInsertErr);
        logStewFailure("stew_assistant_message_insert_failed", aInsertErr, {
          conversation_id: convId,
        });
        toast({
          title: "Stew's reply didn't save",
          description:
            "You'll still see it here. Send another message to keep going.",
          variant: "destructive",
        });
      }

      if (offer && inserted?.id) offer.messageId = inserted.id;

      setMessages((prev) => [
        ...prev,
        {
          id: inserted?.id,
          role: "assistant",
          content: answer,
          sources,
          offer,
          tools,
          crisisFlag: (data?.flagged_crisis as "hard" | "soft" | false) || false,
          deflection: !!data?.deflection,
        },
      ]);

      // Hands-free: if this send came from voice input and voice replies are
      // on, speak the reply. Text-only; no audio is ever recorded or stored.
      if (wasVoice && voiceRepliesOn && ttsSupported) {
        const spoken = markdownToSpeech(answer);
        if (spoken) {
          speak(spoken);
          setSpeakingMessageId(inserted?.id ?? null);
        }
      }

      // Consume the revision context whether or not classifier succeeded.
      setPendingRevision(null);
      onConversationTouched();
    } catch (e: any) {
      console.error("StewChat send failed:", e);
      logStewFailure("stew_reply_failed", e, {
        conversation_id: convId,
      });
      // Keep the member's bubble visible with a retry affordance instead of
      // silently deleting it. Do NOT roll back the user row in the database.
      markFailed();
    } finally {
      setSending(false);
    }
  }, [
    input,
    attachments,
    sending,
    user,
    messages,
    ensureConversation,
    toast,
    onConversationTouched,
    classifyIntent,
    pendingRevision,
    includeUnpublished,
    disableDrafting,
    voiceRepliesOn,
    ttsSupported,
  ]);

  const retryFailed = useCallback(
    (m: StewMessage) => {
      if (!m.retry || !m.clientId) return;
      void send(m.retry.text, {
        attachments: m.retry.attachments,
        clientId: m.clientId,
      });
    },
    [send],
  );

  // Auto-send an incoming initialMessage (e.g. from ?ask= handoff) exactly once.
  const consumedInitialRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialMessage) return;
    if (!user) return;
    if (sending) return;
    if (consumedInitialRef.current === initialMessage) return;
    consumedInitialRef.current = initialMessage;
    void send(initialMessage);
    onInitialMessageConsumed?.();
  }, [initialMessage, user, sending, send, onInitialMessageConsumed]);



  const startDraft = useCallback(
    async (offer: IntentOffer, offerKey: string) => {
      if (!conversationId || !unaId || drafting) return;
      setDrafting(offerKey);
      try {
        const { data, error } = await supabase.functions.invoke(
          "stew-generate-document",
          {
            body: {
              unaId,
              conversationId,
              messageId: offer.messageId ?? null,
              sourceText: offer.sourceText,
              classification: offer.classification,
              revisionOf: offer.revisionOf ?? null,
              revisionNote: null,
            },
          },
        );
        if (error) throw error;
        const d = (data as any)?.document;
        const reg = (data as any)?.registry;
        if (!d) throw new Error("No document returned");
        setReviewDoc({
          id: d.id,
          una_id: d.una_id || unaId,
          document_name: d.document_name,
          document_type: d.document_type,
          custom_content: d.custom_content || "",
          custom_rendered_content: d.custom_rendered_content || null,
          status: d.status,
          version: d.version || 1,
          requires_signatures: !!d.requires_signatures,
          registry_display_name:
            reg?.display_name || offer.registryDisplayName || "Document",
        });
        setReviewOpen(true);

        // Mark this offer as used so the button disappears.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === offer.messageId && m.offer
              ? { ...m, offer: { ...m.offer, used: true } }
              : m,
          ),
        );
      } catch (e: any) {
        console.error("StewChat startDraft failed:", e);
        toast({
          title: "That draft didn't come through",
          description:
            "Try once more, and if it keeps happening, open a ticket.",
          variant: "destructive",
        });
      } finally {
        setDrafting(null);
      }
    },
    [conversationId, unaId, drafting, toast],
  );

  const handleRequestChanges = useCallback(
    (doc: StewGeneratedDoc, note: string) => {
      setReviewOpen(false);
      setReviewDoc(null);
      setPendingRevision({
        parentId: doc.id,
        note,
        registryDisplayName: doc.registry_display_name,
      });
      // Prefill input so member sees context; they can edit or hit send.
      setInput(note);
      setTimeout(() => inputRef.current?.focus(), 50);
      toast({
        title: "Sent back to Stew",
        description: "Add anything else, then send to regenerate.",
      });
    },
    [toast],
  );

  const postAssistantMessage = useCallback(
    async (content: string, metadata: Record<string, any>) => {
      if (!conversationId) return;
      const { data: inserted } = await supabase
        .from("stew_messages")
        .insert({
          conversation_id: conversationId,
          role: "assistant",
          content,
          sources: [] as any,
          metadata: metadata as any,
        })
        .select("id")
        .single();
      const documentId =
        metadata?.signed_document_id ||
        metadata?.approved_document_id ||
        undefined;
      setMessages((prev) => [
        ...prev,
        { id: inserted?.id, role: "assistant", content, documentId },
      ]);
      onConversationTouched();
    },
    [conversationId, onConversationTouched],
  );

  const runFiling = useCallback(
    async (
      documentId: string,
    ): Promise<{
      vault_lane: string;
      display_name: string;
      mirror_status: "delivered" | "failed" | "skipped";
    } | null> => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "stew-file-document",
          { body: { documentId } },
        );
        if (error) throw error;
        return {
          vault_lane: (data as any)?.vault_lane || "The Record",
          display_name: (data as any)?.display_name || "document",
          mirror_status: ((data as any)?.mirror_status || "skipped") as
            | "delivered"
            | "failed"
            | "skipped",
        };
      } catch (e) {
        console.error("stew-file-document failed", e);
        return null;
      }
    },
    [],
  );

  const handleApproved = useCallback(
    async (doc: StewGeneratedDoc, requiresSignature: boolean) => {
      if (!conversationId) return;
      setReviewOpen(false);
      setReviewDoc(null);

      if (requiresSignature) {
        toast({ title: "Approved. Preparing your signature." });
        setSignTarget({
          id: doc.id,
          document_name: doc.document_name,
          document_type: doc.document_type,
          una_id: doc.una_id,
          version: doc.version,
          registry_display_name: doc.registry_display_name,
        });
        setSignOpen(true);
        return;
      }

      toast({ title: "Approved. Filing it now." });

      const filing = await runFiling(doc.id);
      const vaultLane = filing?.vault_lane || "The Record";
      const displayName = filing?.display_name || doc.document_name || "your document";
      const mirrorLine =
        filing?.mirror_status === "delivered"
          ? " and a review copy is on file with your Wealthy Steward advisor"
          : "";
      const confirm =
        `Approved and recorded. Your ${displayName} is filed in The Record under ${vaultLane}${mirrorLine}.\n\n` +
        `[Open in The Record](/governance/record?doc=${doc.id})`;
      await postAssistantMessage(confirm, {
        action: "approved_filed",
        approved_document_id: doc.id,
        vault_lane: vaultLane,
      });
    },
    [conversationId, postAssistantMessage, runFiling, toast],
  );

  const handleSigned = useCallback(
    async (target: { id: string; document_name: string }) => {
      try {
        const filing = await runFiling(target.id);
        const vaultLane = filing?.vault_lane || "The Record";
        const displayName = filing?.display_name || target.document_name || "your document";
        const mirrorLine =
          filing?.mirror_status === "delivered"
            ? " and a review copy is on file with your Wealthy Steward advisor"
            : "";
        const confirm =
          `Signed and recorded. Your ${displayName} is finalized and filed in The Record under ${vaultLane}${mirrorLine}.\n\n` +
          `[Open in The Record](/governance/record?doc=${target.id})`;
        await postAssistantMessage(confirm, {
          action: "signed_filed",
          signed_document_id: target.id,
          vault_lane: vaultLane,
        });
      } catch (e: any) {
        toast({
          title: "That did not go through",
          description: e?.message || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setSignOpen(false);
        setSignTarget(null);
      }
    },
    [runFiling, postAssistantMessage, toast],
  );

  const handleSignCancel = useCallback(() => {
    setSignOpen(false);
    setSignTarget(null);
  }, []);

  const [confirmEscalation, setConfirmEscalation] = useState<string | null>(null);
  // One key per assistant reply being escalated, kept across retries so a
  // second attempt reuses the first attempt's ticket instead of opening
  // another one at the help desk.
  const escalationKeys = useRef<Record<string, string>>({});
  const escalationInFlight = useRef<Set<string>>(new Set());

  const sendEscalation = useCallback(
    async (assistantMessageId: string) => {
      if (!user) return;
      if (escalationInFlight.current.has(assistantMessageId)) return;
      escalationInFlight.current.add(assistantMessageId);
      if (!escalationKeys.current[assistantMessageId]) {
        escalationKeys.current[assistantMessageId] = newSubmissionKey();
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId
            ? { ...m, escalation: { sent: false, sending: true } }
            : m,
        ),
      );
      try {
        // Build transcript from the whole conversation up to this reply.
        const idx = messages.findIndex((m) => m.id === assistantMessageId);
        const upTo = idx >= 0 ? messages.slice(0, idx + 1) : messages;
        const lines: string[] = [];
        for (const m of upTo) {
          const who = m.role === "user" ? "Member" : "Stew";
          lines.push(`${who}: ${m.content}`);
          if (m.attachments && m.attachments.length > 0) {
            lines.push(
              `  (attachments: ${m.attachments.map((a) => a.name).join(", ")})`,
            );
          }
        }
        const transcript = lines.join("\n\n");
        const subject = "Escalated from Stew";

        // Reuse the private Ask a person path exactly:
        // 1) profile lookup for name/email
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name, email")
          .eq("id", user.id)
          .maybeSingle();

        // 2) support_tickets row, idempotent per escalation attempt
        const ticket = await createSupportTicket({
          userId: user.id,
          subject,
          message: transcript,
          idempotencyKey: escalationKeys.current[assistantMessageId],
        });

        // 3) congregation_questions row on the private route
        const { data: inserted, error: qErr } = await supabase
          .from("congregation_questions")
          .insert({
            user_id: user.id,
            question_text: transcript,
            route: "private",
            ticket_id: ticket.id,
          } as any)
          .select("id")
          .single();
        if (qErr) throw qErr;

        // 4) only once the record is safely saved, hand it to the help desk
        syncTicketToHelpDesk({
          ticketId: ticket.id,
          subject,
          message: transcript,
          userEmail: profile?.email || user.email || "",
          userName: profile?.full_name || "Member",
          category: "General",
          priority: "medium",
        });


        // 5) escalation_log via SECURITY DEFINER RPC (stamps identity server-side)
        const firstMemberMsg = upTo.find((m) => m.role === "user");
        await supabase.rpc("log_member_escalation", {
          _question: firstMemberMsg?.content || transcript.slice(0, 500),
          _question_id: inserted.id,
          _una_id: unaId,
        });

        // 6) non-blocking notify
        supabase.functions
          .invoke("notify-question-submitted", {
            body: { question_id: inserted.id },
          })
          .catch((e) => console.error("notify-question-submitted failed:", e));

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? { ...m, escalation: { sent: true, sending: false } }
              : m,
          ),
        );
        setConfirmEscalation(null);
        // Sent for good: a later escalation of this reply starts a new key.
        delete escalationKeys.current[assistantMessageId];
      } catch (e: any) {
        console.error("sendEscalation failed:", e);
        toast({
          title: "That didn't send",
          description: "Try once more in a moment.",
          variant: "destructive",
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? { ...m, escalation: { sent: false, sending: false } }
              : m,
          ),
        );
      } finally {
        escalationInFlight.current.delete(assistantMessageId);
      }

    },
    [user, messages, unaId, toast],
  );



  const showWelcome = messages.length === 0 && !loadingHistory;
  const hasMemberMessage = messages.some((m) => m.role === "user");
  const showStarters = showWelcome && !conversationId;

  const STARTERS = [
    "What should I be doing this month?",
    "How do I record a meeting?",
    "What can the Association pay for?",
  ];

  return (
    <div className={fillContainer
      ? "relative flex flex-col h-full bg-card"
      : "relative flex flex-col h-[calc(100dvh-9rem)] md:h-auto md:min-h-[520px] md:max-h-[calc(100dvh-14rem)] bg-card border rounded-lg md:overflow-hidden"}>

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-scroll md:overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]"
      >
        <div className="mx-auto max-w-3xl px-3 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
            {showWelcome && (
              <div className="flex justify-start items-start gap-3">
                <StewAvatar size={32} />
                <div className="max-w-[92%] text-sm md:text-base leading-relaxed text-foreground pt-1">
                  <ReactMarkdown>{welcome.content}</ReactMarkdown>
                  {showStarters && !hasMemberMessage && (
                    <div className="mt-3 hidden md:flex flex-wrap gap-2">
                      {STARTERS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => void send(s)}
                          disabled={sending}
                          className="text-xs md:text-sm rounded-full border border-muted-foreground/30 hover:border-foreground/50 hover:bg-muted/60 px-3 py-1.5 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}



            {loadingHistory && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading conversation
              </div>
            )}

            {messages.map((m, i) => {
              const offerKey = m.id ?? m.clientId ?? `local-${i}`;
              const offerLabel = m.offer
                ? RECORD_LABEL[m.offer.classification.record_type]
                : null;
              const priorUser = m.role === "assistant"
                ? [...messages.slice(0, i)].reverse().find((x) => x.role === "user")
                : null;
              return (
                <div
                  key={offerKey}
                  className={
                    m.role === "user"
                      ? "flex justify-end"
                      : "flex justify-start"
                  }
                >
                  {m.role === "user" ? (
                    <div className="max-w-[85%] flex flex-col items-end gap-1">
                      <div className="rounded-2xl bg-primary text-primary-foreground px-4 py-2.5 text-sm md:text-base whitespace-pre-wrap shadow-sm">
                        {m.content}
                      </div>
                      {m.failed && (
                        <button
                          type="button"
                          onClick={() => retryFailed(m)}
                          className="text-xs text-destructive hover:underline"
                        >
                          That didn't send. Tap to try again.
                        </button>
                      )}
                    </div>

                  ) : (() => {
                    // Display only: internal reference codes never reach members.
                    const titleMap: Record<string, string> = { ...docTitles };
                    for (const s of m.sources || []) {
                      if (s.doc_id) titleMap[s.doc_id] = stripDocIdPrefix(s.title);
                    }
                    const shown = stripDocCodes(m.content || "", titleMap);
                    return (
                    <div className="flex items-start gap-3 max-w-[95%] w-full">
                      <StewAvatar size={32} />
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="prose prose-sm md:prose-base max-w-none text-foreground prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2 prose-a:text-primary flex-1 min-w-0">
                            <ReactMarkdown>{shown}</ReactMarkdown>
                          </div>
                          {ttsSupported && m.content && (
                            <button
                              type="button"
                              aria-label={
                                speakingMessageId === (m.id ?? offerKey)
                                  ? "Stop reading"
                                  : "Read aloud"
                              }
                              onClick={() => {
                                const key = m.id ?? offerKey;
                                if (speakingMessageId === key) {
                                  stopSpeaking();
                                  setSpeakingMessageId(null);
                                  return;
                                }
                                const text = markdownToSpeech(shown);
                                if (!text) return;
                                speak(text);
                                setSpeakingMessageId(key);
                              }}

                              className="mt-1 shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            >
                              {speakingMessageId === (m.id ?? offerKey) ? (
                                <VolumeX className="h-3.5 w-3.5" />
                              ) : (
                                <Volume2 className="h-3.5 w-3.5" />
                              )}
                            </button>
                          )}
                        </div>
                      {m.role === "assistant" && m.id && user && (m.crisisFlag === "hard" || m.deflection) && (
                        (() => {
                          const sent = m.escalation?.sent;
                          const sending = m.escalation?.sending;
                          const showCard = m.crisisFlag === "hard" || confirmEscalation === m.id;
                          if (sent) {
                            return (
                              <div className="mt-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                                Sent. The team will reply by email.
                              </div>
                            );
                          }
                          if (!showCard) {
                            return (
                              <div className="pt-1">
                                <button
                                  type="button"
                                  onClick={() => setConfirmEscalation(m.id!)}
                                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                                >
                                  Want to ask a person instead?
                                </button>
                              </div>
                            );
                          }
                          return (
                            <div className="mt-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2.5 space-y-2">
                              <p className="text-sm font-medium text-foreground">
                                This is one for the team.
                              </p>
                              <p className="text-xs text-muted-foreground">
                                You can send this conversation to the Wealthy Steward team. They will see the messages in this chat, including anything you attached. Nothing is sent until you confirm.
                              </p>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => sendEscalation(m.id!)}
                                  disabled={sending}
                                  className="gap-1.5"
                                >
                                  {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                  Send to the team
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setConfirmEscalation(null)}
                                  disabled={sending}
                                >
                                  Not now
                                </Button>
                              </div>
                            </div>
                          );
                        })()
                      )}


                      {!disableDrafting && m.offer && !m.offer.used && offerLabel && (
                        <div className="pt-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => startDraft(m.offer!, offerKey)}
                            disabled={drafting === offerKey}
                            className="gap-1.5"
                          >
                            {drafting === offerKey ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <FilePlus2 className="h-3.5 w-3.5" />
                            )}
                            {offerLabel}
                          </Button>
                        </div>
                      )}

                      {(() => {
                        if (m.deflection) return null;
                        if (!m.sources || m.sources.length === 0) return null;
                        const named = m.sources
                          .map((s) => ({ s, name: stripDocIdPrefix(s.title) }))
                          .filter((x) => x.name && x.name.length > 0)
                          .slice(0, 3);
                        if (named.length === 0) return null;
                        return (
                          <div className="pt-1 text-xs text-muted-foreground">
                            <span className="mr-1">Read more:</span>
                            {named.map(({ s, name }, i) => {
                              const isDraft =
                                surface === "internal" &&
                                s.status !== undefined &&
                                s.status !== "published";
                              return (
                                <span key={s.doc_id}>
                                  {i > 0 && <span>, </span>}
                                  <Link
                                    to={sourceHref(s)}
                                    className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                                  >
                                    {name}
                                  </Link>
                                  {isDraft && (
                                    <span className="ml-1 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100 text-[10px] font-medium px-1.5 py-0.5 uppercase tracking-wide">
                                      {s.status === "draft" ? "Draft" : s.status}
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {surface === "member" && m.tools && m.tools.length > 0 && (() => {
                        const hidden = new Set(hiddenToolKeys ?? []);
                        const visibleTools = m.tools.filter((k) => !hidden.has(k)).slice(0, 2);
                        if (visibleTools.length === 0) return null;
                        return (
                          <div className="pt-2 flex flex-col sm:flex-row flex-wrap gap-2">
                            {visibleTools.map((key) => {
                              const tool = STEW_TOOL_REGISTRY[key];
                              if (!tool) return null;
                              const Icon = tool.icon;
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => navigate(tool.route)}
                                  className="inline-flex min-h-[44px] w-full sm:w-auto items-center justify-between gap-2 rounded-full border border-border/60 bg-background hover:bg-muted transition-colors px-4 py-2 text-sm font-medium text-foreground shadow-sm"
                                >
                                  <span className="inline-flex items-center gap-2 min-w-0">
                                    <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                                    <span className="truncate">{tool.label}</span>
                                  </span>
                                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}




                      {m.documentId && (
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={async () => {
                              const id = m.documentId!;
                              setDownloadingDocId(id);
                              try {
                                await downloadDocumentById(id);
                              } catch (e: any) {
                                console.error("StewChat download failed:", e);
                                toast({
                                  title: "That download didn't start",
                                  description:
                                    "Try once more, and if it keeps happening, open a ticket.",
                                  variant: "destructive",
                                });
                              } finally {
                                setDownloadingDocId(null);
                              }
                            }}
                            disabled={downloadingDocId === m.documentId}
                            className="inline-flex items-center gap-1 rounded-full border bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground text-[11px] px-2.5 py-1 transition-colors disabled:opacity-60"
                          >
                            {downloadingDocId === m.documentId ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Download className="h-3 w-3" />
                            )}
                            Download
                          </button>
                        </div>
                      )}

                        {priorUser && m.content && (
                          <StewFeedbackControl
                            surface={surface}
                            conversationId={conversationId}
                            messageId={m.id}
                            question={priorUser.content}
                            answer={m.content}
                            sources={m.sources ?? []}
                          />
                        )}
                      </div>
                    </div>
                    );
                  })()}



                </div>
              );
            })}

            {sending && (
              <div className="flex justify-start">
                <div
                  className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-2"
                  role="status"
                  aria-live="polite"
                >
                  <span className="sr-only">Stew is writing</span>
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce" aria-hidden="true" />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce [animation-delay:120ms]" aria-hidden="true" />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce [animation-delay:240ms]" aria-hidden="true" />
                </div>
              </div>
            )}
          </div>
      </div>

      {showJumpToLatest && (
        <button
          type="button"
          onClick={() => {
            const el = scrollRef.current;
            if (!el) return;
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
          }}
          className="absolute left-1/2 -translate-x-1/2 bottom-[calc(env(safe-area-inset-bottom,0px)+8.5rem)] md:bottom-28 z-20 inline-flex items-center gap-1.5 rounded-full border bg-background/95 backdrop-blur shadow-md text-xs px-3 py-1.5 hover:bg-muted"
          aria-label="Jump to latest message"
        >
          <ArrowDown className="h-3.5 w-3.5" />
          Jump to latest
        </button>
      )}

      <div className="border-t bg-background/60 backdrop-blur-sm pb-[env(safe-area-inset-bottom,0px)]">
        {(voice.recording || voice.transcribing) && (
          <div className="mx-auto max-w-3xl px-3 md:px-6 pt-2">
            {voice.recording ? (
              <button
                type="button"
                onClick={() => voice.stop()}
                className="inline-flex items-center gap-2 text-xs text-destructive"
                aria-live="polite"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" />
                </span>
                {voice.elapsed >= RECORD_WARN_AT
                  ? `Recording... ${voice.remaining}s left, tap to stop`
                  : "Recording... tap to stop"}
              </button>
            ) : (
              <span className="inline-flex items-center gap-2 text-xs text-muted-foreground" aria-live="polite">
                <Loader2 className="h-3 w-3 animate-spin" />
                Transcribing...
              </span>
            )}
          </div>
        )}
        {isMobile && showStarters && !hasMemberMessage && (
          <div className="pt-2 -mx-0.5">
            <div className="mx-auto max-w-3xl px-3 flex gap-2 overflow-x-auto touch-pan-x [-webkit-overflow-scrolling:touch] no-scrollbar">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => void send(s)}
                  disabled={sending}
                  className="shrink-0 text-xs rounded-full border border-muted-foreground/30 hover:border-foreground/50 bg-background px-3 py-1.5 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {ttsSupported && !isMobile && (
          <div className="mx-auto max-w-3xl px-4 md:px-6 pt-2 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setVoiceRepliesOn((v) => {
                  const next = !v;
                  if (!next) {
                    stopSpeaking();
                    setSpeakingMessageId(null);
                  }
                  return next;
                });
              }}
              className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              aria-pressed={voiceRepliesOn}
            >
              {voiceRepliesOn ? (
                <Volume2 className="h-3 w-3" />
              ) : (
                <VolumeX className="h-3 w-3" />
              )}
              Voice replies {voiceRepliesOn ? "on" : "off"}
            </button>
          </div>
        )}
        {pendingRevision && (
          <div className="mx-auto max-w-3xl px-3 md:px-6 pt-2 text-[11px] text-muted-foreground">
            Revising {pendingRevision.registryDisplayName}. Send to regenerate.
          </div>
        )}
        {attachments.length > 0 && (
          <div className="mx-auto max-w-3xl px-3 md:px-6 pt-2 flex flex-wrap gap-1.5">
            {attachments.map((a) => (
              <span
                key={a.id}
                className="inline-flex items-center gap-1 rounded-full border bg-muted/40 text-muted-foreground text-[11px] px-2 py-1"
              >
                {a.kind === "image" ? (
                  <ImageIcon className="h-3 w-3" />
                ) : (
                  <FileText className="h-3 w-3" />
                )}
                <span className="max-w-[160px] truncate">{a.file.name}</span>
                {a.truncated && (
                  <span className="text-amber-700 dark:text-amber-300">truncated</span>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setAttachments((prev) => prev.filter((x) => x.id !== a.id))
                  }
                  aria-label={`Remove ${a.file.name}`}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
          className="mx-auto max-w-3xl px-2.5 md:px-6 py-2 md:py-3 flex flex-col md:flex-row md:items-end gap-1.5 md:gap-2"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            hidden
            onChange={async (e) => {
              const list = Array.from(e.target.files ?? []);
              e.target.value = "";
              if (list.length === 0) return;
              const remaining = MAX_FILES_PER_MESSAGE - attachments.length;
              if (remaining <= 0) {
                toast({
                  title: "That's the file limit",
                  description: `You can attach up to ${MAX_FILES_PER_MESSAGE} files per message.`,
                  variant: "destructive",
                });
                return;
              }
              const toAdd = list.slice(0, remaining);
              setPreparingFile(true);
              try {
                for (const f of toAdd) {
                  if (f.size > MAX_FILE_BYTES) {
                    toast({
                      title: "That file is too large",
                      description: `${f.name} is over 10 MB.`,
                      variant: "destructive",
                    });
                    continue;
                  }
                  if (!detectKind(f) || (!ACCEPTED_MIME.includes(f.type) && !/\.(pdf|txt|md|markdown|docx|png|jpe?g)$/i.test(f.name))) {
                    toast({
                      title: "That file type isn't supported",
                      description: `${f.name}: PDF, text, markdown, Word, PNG, or JPG only.`,
                      variant: "destructive",
                    });
                    continue;
                  }
                  try {
                    const prepared = await prepareAttachment(f);
                    setAttachments((prev) => [...prev, prepared]);
                  } catch (err: any) {
                    console.error("prepareAttachment failed", err);
                    toast({
                      title: "Couldn't read that file",
                      description: `${f.name}: ${err?.message || "unreadable"}`,
                      variant: "destructive",
                    });
                  }
                }
              } finally {
                setPreparingFile(false);
              }
            }}
          />
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // Desktop: Enter sends, Shift+Enter adds a new line.
              // Mobile: Enter always adds a new line; the send button sends.
              if (e.key === "Enter" && !e.shiftKey && !isMobile) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder={isMobile ? "Ask Stew anything" : "Ask Stew anything, or attach a file for context."}
            rows={1}
            disabled={sending}
            className="flex-1 order-1 md:order-2 w-full min-h-[44px] max-h-[164px] resize-none py-2.5 px-3 leading-6 text-base"
          />

          <div className="order-2 md:order-1 md:contents flex items-center gap-1.5">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-9 w-9 md:h-11 md:w-11 shrink-0"
              aria-label="Attach files"
              disabled={sending || preparingFile || attachments.length >= MAX_FILES_PER_MESSAGE}
              onClick={() => fileInputRef.current?.click()}
            >
              {preparingFile ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Paperclip className="h-4 w-4" />
              )}
            </Button>
            {voice.supported && (
              <Button
                type="button"
                size="icon"
                variant={voice.recording ? "destructive" : "ghost"}
                className={
                  "h-9 w-9 md:h-11 md:w-11 shrink-0 " +
                  (voice.recording ? "animate-pulse" : "")
                }
                aria-label={voice.recording ? "Stop recording" : "Start voice input"}
                aria-pressed={voice.recording}
                disabled={sending || voice.transcribing}
                onClick={() => {
                  if (voice.recording) {
                    voice.stop();
                  } else {
                    voice.start();
                  }
                }}
              >
                {voice.transcribing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : voice.recording ? (
                  <Square className="h-4 w-4 fill-current" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            )}

            {ttsSupported && isMobile && (
              <button
                type="button"
                onClick={() => {
                  setVoiceRepliesOn((v) => {
                    const next = !v;
                    if (!next) {
                      stopSpeaking();
                      setSpeakingMessageId(null);
                    }
                    return next;
                  });
                }}
                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-1.5"
                aria-pressed={voiceRepliesOn}
              >
                {voiceRepliesOn ? (
                  <Volume2 className="h-3.5 w-3.5" />
                ) : (
                  <VolumeX className="h-3.5 w-3.5" />
                )}
                <span className="sr-only md:not-sr-only">Voice replies {voiceRepliesOn ? "on" : "off"}</span>
              </button>
            )}
            <div className="flex-1 md:hidden" />
            {(() => {
              const canSend = !sending && (input.trim().length > 0 || attachments.length > 0);
              return (
            <Button
              type="submit"
              size="icon"
              disabled={!canSend}
              className={`h-9 w-9 md:h-11 md:w-11 shrink-0 order-3 transition-colors ${
                canSend
                  ? "bg-[hsl(var(--deep-green))] text-[hsl(var(--deep-green-foreground))] hover:bg-[hsl(var(--deep-green-hover))]"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
              aria-label="Send"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
              );
            })()}
          </div>
        </form>
        <p className="mx-auto max-w-3xl px-3 md:px-6 pb-2 md:pb-3 text-[10px] md:text-[11px] leading-tight text-muted-foreground text-center">
          {includeUnpublished
            ? "Stew answers from the full manual, including drafts. Educational, not legal or tax advice."
            : "Answers from your published manual and help library. Educational, not legal or tax advice."}
          {!includeUnpublished && (
            <>
              {" "}
              Private to you. The team only sees a chat if you send it to them.
            </>
          )}
        </p>

      </div>


      <StewDocumentReview
        open={reviewOpen}
        doc={reviewDoc}
        onClose={() => setReviewOpen(false)}
        onRequestChanges={handleRequestChanges}
        onApproved={handleApproved}
      />

      <StewSignFlow
        open={signOpen}
        target={signTarget}
        onCancel={handleSignCancel}
        onSigned={handleSigned}
      />
    </div>
  );
}
